<template>
    <div class="preflight-preview">
        <div v-if="previewUrl" class="preflight-preview-asset">
            <img
                v-if="assetType === 'IMAGE'"
                class="preflight-preview-image"
                :src="previewUrl"
                alt="Rendered asset preview"
            />

            <video
                v-if="assetType === 'VIDEO'"
                class="preflight-preview-video"
                :src="previewUrl"
                controls
                muted
                alt="Rendered asset preview"
            />
        </div>

        <template v-if="error">
            <div class="preflight-info">
                <h4>There was an error processing the {{ platformLabel }}</h4>
                <h5>Analysis requested at {{ new Date(preflightHistory.created).toLocaleString() }}</h5>
            </div>
            <p class="preflight-info">
                Status: <span class="preflight-error-message">{{ error }}</span>
            </p>
        </template>

        <template v-if="pending">
            <div class="preflight-info">
                <h5>Analysis requested at {{ new Date(preflightHistory.created).toLocaleString() }}</h5>
            </div>
            <p class="preflight-info">Status: {{ platformLabel }} check is still in progress...</p>
        </template>

        <CreativeXReport v-if="creativeXReport" :report="creativeXReport.report" :created="creativeXReport.created" />

        <SataliaReport v-if="sataliaReport" :report="sataliaReport.report" :created="sataliaReport.created" />
    </div>
</template>
<script lang="ts" setup>
import { ref, computed } from "vue";

import type {
    PreflightHistory,
    CreativeXPreflightHistory,
    SataliaPreflightHistory
} from "@/utils/Preflight/getPreflightHistory";
import { PreflightStatus } from "hoxton-shared/enums/creativex";
import { PLATFORM } from "hoxton-shared/enums/exports";

import CreativeXReport from "./CreativeXReport.vue";
import SataliaReport from "./SataliaReport.vue";

const props = defineProps<{
    preflightHistory: PreflightHistory;
    platform: typeof PLATFORM.CREATIVEX | typeof PLATFORM.SATALIA;
}>();

const isCreativeX = computed(() => props.platform === PLATFORM.CREATIVEX);
const isSatalia = computed(() => props.platform === PLATFORM.SATALIA);

const platformLabel = computed(() => (isCreativeX.value ? "preflight" : "Pre-flight"));

const previewUrl = ref(props.preflightHistory.previewUrl);

const error = computed(() => {
    if (props.preflightHistory.status === PreflightStatus.ERROR) {
        return props.preflightHistory.error;
    }
    return null;
});

const pending = computed(() => {
    return props.preflightHistory.status === PreflightStatus.PROCESSING;
});

const creativeXReport = computed(() => {
    if (isCreativeX.value && props.preflightHistory.status === PreflightStatus.COMPLETED) {
        return props.preflightHistory as Extract<
            CreativeXPreflightHistory,
            { status: typeof PreflightStatus.COMPLETED }
        >;
    }
    return null;
});

const sataliaReport = computed(() => {
    if (isSatalia.value && props.preflightHistory.status === PreflightStatus.COMPLETED) {
        return props.preflightHistory as Extract<SataliaPreflightHistory, { status: typeof PreflightStatus.COMPLETED }>;
    }
    return null;
});

const assetType = computed(() => {
    if (previewUrl.value?.includes(".mp4")) {
        return "VIDEO";
    }

    return "IMAGE";
});
</script>

<style scoped>
.preflight-preview {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.preflight-error-message {
    color: var(--wpp-danger-color-400);
}
.preflight-preview-asset {
    display: flex;
    justify-content: center;
    align-items: center;
}

.preflight-preview-asset,
.preflight-guidelines,
.preflight-info {
    padding: 16px;
    border-radius: 4px;
    background-color: var(--wpp-grey-color-100);
    overflow: hidden;
}

.preflight-preview-image,
.preflight-preview-video {
    max-width: 600px;
    max-height: 400px;
}

.preflight-info-table-row {
    font-size: 12px;
    display: flex;
}

.preflight-info-table-row > span:first-child {
    flex: 0 0 140px;
}

.preflight-info > h4 {
    margin-bottom: 8px;
}

.preflight-guidelines table {
    margin: 0;
    padding: 0;
    width: 100%;

    border-collapse: collapse;
}

.preflight-guidelines th,
.preflight-guidelines td {
    padding: 8px;
}

.preflight-guidelines th {
    text-align: left;
}

body:has(> .wpp-theme-dark) {
    .preflight-preview-asset,
    .preflight-guidelines,
    .preflight-info {
        background-color: var(--wpp-grey-color-900);
    }
}
</style>
